import { createContext, useContext } from 'react'
import type { AggregationFunc, FluxDuration, FluxUnit } from '../time'

/**
 * `au/ui/labels` — **todo** el texto de interfaz que la capa escribe por su cuenta, en un solo
 * catálogo tipado. Es el único archivo de `src/au/ui` con copy: `audit:copy` falla ante un literal
 * de interfaz en cualquier otro (ADR-0031).
 *
 * **Qué entra y qué no.** Entra el texto que la capa pone sin que la página lo pida: el nombre de
 * un botón interno (la ✕ de un chip, el «Sumar» de un campo numérico), un rótulo fijo («Desde»,
 * «Aplicar»), el `aria-valuetext` de un pulgar. Ese texto **no tiene prop por instancia**: se
 * cambia una sola vez, montando `AuLabelsProvider`. El contenido que decide cada pantalla
 * —`placeholder`, `emptyMessage`— sigue siendo prop y solo toma su default de acá.
 *
 * **Lo que no es copy se queda donde está.** `locale` y `timeZone` son parámetros obligatorios de
 * los componentes que los usan (ADR-0023) y no viajan por el provider; los nombres de mes, de día
 * y de unidad los produce `Intl` con ese `locale`. Por eso varias entradas son funciones: reciben
 * el texto que `Intl` ya formateó y le agregan lo que `Intl` no sabe —el artículo, el género, el
 * «Hoy»—.
 *
 * **Sin provider rige `esLabels`**, el español de la capa. Así el catálogo de componentes, los
 * verificadores SSR y cualquier consumidor en español funcionan sin montar nada.
 */
export interface AuLabels {
  select: {
    /** Default del `placeholder` del trigger. La prop del mismo nombre lo pisa. */
    placeholder: string
  }
  combobox: {
    /** Default del `placeholder` del input. La prop del mismo nombre lo pisa. */
    placeholder: string
    /** Default de `emptyMessage`. La prop lo pisa, y conviene: cada lista sabe qué le falta. */
    empty: string
    /** El botón que vacía la selección. */
    clear: string
    /** El chevron que abre la lista. */
    open: string
    /** La ✕ de cada chip del multi. Recibe la etiqueta del item: «Quitar Caudal». */
    remove: (label: string) => string
  }
  numberField: {
    decrement: string
    increment: string
    /** `aria-roledescription` del input. Pisa el «Number field» que Base UI escribe en inglés. */
    roleDescription: string
  }
  slider: {
    /** Nombre de cada pulgar de un rango. Con un solo pulgar no se usa: el nombre es el `label`. */
    thumb: (index: number, count: number) => string
    /**
     * `aria-valuetext` de cada pulgar de un rango. Recibe el valor ya formateado por Base UI. Pisa
     * el «start range»/«end range» en inglés que la librería agrega a los rangos.
     */
    valueText: (formatted: string, index: number, count: number) => string
  }
  chip: {
    /**
     * El verbo de la ✕. No es el nombre entero: el botón se nombra por `aria-labelledby` con esto
     * **y** el contenido del chip, así que anuncia «Quitar Caudal» sin que la página pase nada.
     */
    remove: string
  }
  dialog: {
    /** La ✕ del encabezado. */
    close: string
  }
  pagination: {
    /** Default de `ariaLabel`, el nombre de la región. La prop lo pisa. */
    navigation: string
    /** Default de `rowsLabel`, el rótulo del selector de filas. La prop lo pisa. */
    rowsPerPage: string
    first: string
    previous: string
    next: string
    last: string
    /** Nombre del botón de cada página: «Página 3 de 12». */
    page: (page: number, total: number) => string
  }
  callout: {
    /** La ✕ de un `Callout` con `onDismiss`. */
    dismiss: string
  }
  toast: {
    /** El botón que cierra un toast. */
    dismiss: string
    /** Nombre de la región de notificaciones. Pisa el «Notifications» de Base UI. */
    viewport: string
  }
  calendar: {
    previous: string
    next: string
    monthDropdown: string
    yearDropdown: string
    /** Nombre de la barra de navegación (las flechas). */
    nav: string
    /** Con `showWeekNumber`: el encabezado de la columna y cada celda. */
    weekNumberHeader: string
    weekNumber: (weekNumber: number) => string
    /** Nombre de cada día. `dateText` es la fecha larga que `Intl` ya formateó con el idioma. */
    dayButton: (dateText: string, modifiers: { today?: boolean; selected?: boolean }) => string
    /** Nombre de cada celda de la grilla, sin la marca de seleccionado. */
    gridcell: (dateText: string, modifiers: { today?: boolean }) => string
  }
  timeQuery: {
    /** Nombre del diálogo del popover. No se dibuja: es para el lector de pantalla. */
    dialogTitle: string
    customRangePlaceholder: string
    customRangeLabel: string
    /** Lo que muestra la lista del medio cuando lo tipeado no es una duración. */
    durationHint: string
    from: string
    to: string
    /** Ejemplos del formato que aceptan los campos absolutos. */
    fromPlaceholder: string
    toPlaceholder: string
    apply: string
    /** Aviso de un rango invertido. */
    rangeError: string
    /** El extremo abierto de un rango, en el trigger: «19 ago 14:00 → ahora». */
    openEnd: string
    /** Rótulo de la columna de agregación. */
    aggregation: string
    /** Nombre accesible del select de función. */
    aggregationTrigger: string
    /** La función, abreviada para el trigger. `avg` no se muestra, pero su entrada existe igual. */
    aggShort: Record<AggregationFunc, string>
    /** El nombre de cada función, en el select. */
    aggLabel: Record<AggregationFunc, string>
    windowAuto: string
    windowNone: string
    /** «Sin agregar», abreviado para el trigger. */
    windowNoneShort: string
    /**
     * `{ 3, 'w' }` → «Últimas 3 semanas», el rótulo de un preset y del trigger. `text` es la
     * duración que `formatDurationText` ya formateó con el `locale` («3 semanas»): lo que falta es
     * el artículo, que concuerda en género y número con la unidad.
     */
    lastN: (duration: FluxDuration, text: string) => string
  }
}

/**
 * El género de cada unidad, que es lo único que `Intl` no puede dar.
 *
 * Un numeral no concuerda en género, así que «3 semanas» sale de `formatDurationText` pero
 * «Últimas 3 semanas» no: el artículo necesita saber que la semana es femenina.
 */
const UNIT_IS_FEMININE: Record<FluxUnit, boolean> = {
  m: false,
  h: true,
  d: false,
  w: true,
  mo: false,
  y: false,
}

/** El catálogo por defecto: el español de la capa. Rige cuando no hay `AuLabelsProvider`. */
export const esLabels: AuLabels = {
  select: {
    placeholder: 'Seleccionar…',
  },
  combobox: {
    placeholder: 'Buscar…',
    empty: 'Sin resultados.',
    clear: 'Limpiar selección',
    open: 'Abrir lista',
    remove: (label) => `Quitar ${label}`,
  },
  numberField: {
    decrement: 'Restar',
    increment: 'Sumar',
    roleDescription: 'Campo numérico',
  },
  slider: {
    thumb: (index, count) => (count === 2 ? (index === 0 ? 'Mínimo' : 'Máximo') : `Valor ${index + 1}`),
    valueText: (formatted) => formatted,
  },
  chip: {
    remove: 'Quitar',
  },
  dialog: {
    close: 'Cerrar',
  },
  pagination: {
    navigation: 'Paginación',
    rowsPerPage: 'Filas por página',
    first: 'Primera página',
    previous: 'Página anterior',
    next: 'Página siguiente',
    last: 'Última página',
    page: (page, total) => `Página ${page} de ${total}`,
  },
  callout: {
    dismiss: 'Descartar aviso',
  },
  toast: {
    dismiss: 'Descartar',
    viewport: 'Notificaciones',
  },
  calendar: {
    previous: 'Ir al mes anterior',
    next: 'Ir al mes siguiente',
    monthDropdown: 'Elegir el mes',
    yearDropdown: 'Elegir el año',
    nav: 'Navegación del calendario',
    weekNumberHeader: 'Número de semana',
    weekNumber: (weekNumber) => `Semana ${weekNumber}`,
    dayButton: (dateText, { today, selected }) => {
      let label = today ? `Hoy, ${dateText}` : dateText
      if (selected) label = `${label}, seleccionado`
      return label
    },
    gridcell: (dateText, { today }) => (today ? `Hoy, ${dateText}` : dateText),
  },
  timeQuery: {
    dialogTitle: 'Consulta temporal',
    customRangePlaceholder: 'Rango personalizado: 3h, 3 horas, 3 m…',
    customRangeLabel: 'Rango personalizado',
    durationHint: 'Escriba una duración: 3h, 15m, 2w.',
    from: 'Desde',
    to: 'Hasta',
    fromPlaceholder: '2026-08-19 14:00',
    toPlaceholder: '2026-08-20 09:30',
    apply: 'Aplicar',
    rangeError: '«Hasta» tiene que ser posterior a «Desde».',
    openEnd: 'ahora',
    aggregation: 'Agregación',
    aggregationTrigger: 'Función de agregación',
    aggShort: { avg: 'prom', max: 'máx', min: 'mín', count: 'cuenta', integral: 'int' },
    aggLabel: { avg: 'Promedio', max: 'Máximo', min: 'Mínimo', count: 'Cuenta', integral: 'Integral' },
    windowAuto: 'Automática',
    windowNone: 'Sin agregar',
    windowNoneShort: 'sin agregar',
    lastN: (duration, text) => {
      const article = UNIT_IS_FEMININE[duration.unit] ? 'Última' : 'Último'
      // «Última hora» y no «Última 1 hora»: con uno el numeral sobra. `Intl` no da el nombre de la
      // unidad suelto, así que se recorta del texto que ya vino formateado.
      if (duration.value === 1) return `${article} ${text.replace(/^\D*\d+\s*/, '')}`
      return `${article}s ${text}`
    },
  },
}

/**
 * El contexto va en este archivo y el provider en `labels-provider.tsx` por
 * `react-refresh/only-export-components`: en `src/au/` la regla está en `error`, y un archivo que
 * exporta un componente no puede exportar además valores ni hooks.
 *
 * `null` es «sin provider», y no el catálogo por defecto, para que Calendar pueda distinguir los dos
 * casos (ver `useHasAuLabelsProvider`).
 */
export const AuLabelsContext = createContext<AuLabels | null>(null)

/** La sección del catálogo vigente: la del provider más cercano, o `esLabels` si no hay ninguno. */
export function useAuLabels<K extends keyof AuLabels>(section: K): AuLabels[K] {
  return (useContext(AuLabelsContext) ?? esLabels)[section]
}

/**
 * ¿Hay un provider montado? Solo lo usa Calendar: sin provider y con un `lang` que no es español,
 * rigen las etiquetas propias de `react-day-picker` en vez de las de `esLabels`, porque aplicar el
 * catálogo español a una grilla en otro idioma mezclaría los dos. Con provider, rige su catálogo.
 */
export function useHasAuLabelsProvider(): boolean {
  return useContext(AuLabelsContext) !== null
}
